import React, { useContext, useMemo } from 'react';

import type { PlanState } from '@aline/common';
import { IconButton, Typography, useTheme } from '@mui/material';
import { Copy, FilePlus2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { AlineAlert } from 'src/components/generic/AlineAlert';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';

import { getAssistantActionMarkdown } from './assistant-action-markdown';
import { getIsWaitingForDocumentExtraction } from './get-is-waiting-for-document-extraction';
import { pickAssistantMessageVariant } from './pickAssistantMessageVariant';
import { PlanTree } from './PlanTree';
import { getIsTerminalAssistantStatus } from '../chat-hooks/harness-visible-work';
import { useAssistantMessageHandlers } from '../chat-hooks/useAssistantMessageHandlers';
import { MentionEntityContext } from '../chat-hooks/useMentionAccessContext';
import { useUpdateFeedback } from '../chat-hooks/useUpdateFeedback';
import { $chatThreadState } from '../chat-state';
import type { ChatAssistantMessage } from '../chat-types';
import { ChatPanelMarkdown } from '../ChatPanelMarkdown/ChatPanelMarkdown';
import { createAIChatItem } from '../ChatPanelMarkdown/helpers/aiChatItem';

const DOCUMENT_EXTRACTION_WAIT_COPY = 'Processing documents, then chat will resume';

export interface ChatDocument extends Pick<Apollo.Document, 'name' | '__typename' | 'id'> {
  latestVersion: Pick<Apollo.FileVersion, '__typename' | 'id'>;
}

export function createChatAssistantMessageElId(
  message: Pick<ChatAssistantMessage, 'id'>,
  threadId: string
) {
  return createAIChatItem({
    messageId: message.id,
    threadId,
    type: 'message',
  });
}

export function ChatPanelAssistantMessage({
  documentInView,
  message,
  threadId,
}: {
  documentInView: ChatDocument | null;
  message: ChatAssistantMessage;
  threadId: UUID;
}) {
  const state = useSnapshot($chatThreadState);
  // Create a unique ID for this message using the same format as the URL parameter.
  const elementId = createChatAssistantMessageElId(message, threadId);

  const isStreaming = message.status === Apollo.AgentMessageStatus.Streaming;
  const decision = pickAssistantMessageVariant(message.status, message.planState);
  /**
   * Pulse rules:
   *   - Only render while THIS message is streaming.
   *   - Only when the loading event was scoped to this message id
   *     (otherwise back-to-back sends could leak the pulse across
   *     turns).
   *   - For the V1 markdown path, only before any token has landed —
   *     the `!message.text` gate hides the pulse as soon as the trivial
   *     response starts streaming.
   *   - For the plan-tree path, never render the pulse here. PlanTree
   *     owns its own "Planning…" indicator while the snapshot is empty,
   *     and `message.text` stays empty the entire turn for plan-tree
   *     content (which flows through `planState`), so a `!message.text`
   *     gate would not suppress it.
   */
  const scopedThinkingMessage =
    isStreaming && state.thinkingMessage?.messageId === message.id
      ? state.thinkingMessage.text
      : undefined;
  const isWaitingForDocuments = getIsWaitingForDocumentExtraction(message);
  const thinkingMessage =
    !isWaitingForDocuments &&
    isStreaming &&
    decision.variant === 'markdown' &&
    !message.text &&
    scopedThinkingMessage
      ? scopedThinkingMessage
      : undefined;

  if (decision.variant === 'plan-tree') {
    return (
      <div id={elementId}>
        {/* PlanTree renders planner-authored markdown from planState plus
            each node's output, so plan turns do not render message.text. */}
        <PlanTree
          documentInView={documentInView}
          message={message}
          planState={decision.planState}
          thinkingMessage={!isWaitingForDocuments ? scopedThinkingMessage : undefined}
          threadId={threadId}
        />
        {!isStreaming && (
          <ChatAssistantActionButtons
            message={message}
            planState={decision.planState}
            threadId={threadId}
          />
        )}
      </div>
    );
  }

  if (decision.variant === 'error') {
    return <AssistantErrorInMessage message={message} />;
  }

  return (
    <div id={elementId}>
      <ChatPanelMarkdown
        completionText={message.text}
        documentInView={documentInView}
        message={message}
        threadId={threadId}
      />
      {!isStreaming && <ChatAssistantActionButtons message={message} threadId={threadId} />}
      {thinkingMessage && <ThinkingMessage thinkingMessage={thinkingMessage} />}
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.common.black,
    },
    // Muted by default; icons draw with currentColor so they follow this and darken on hover.
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.75),
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0),
    /**
     * These icons belong to the assistant message above them. The markdown
     * renderer drops the trailing paragraph's bottom margin so they already
     * sit close to the text; add space below to separate them from the next
     * (user) message.
     */
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
}));

function ChatAssistantActionButtons({
  message,
  planState,
  threadId,
}: {
  message: ChatAssistantMessage;
  threadId: string;
  planState?: PlanState;
}) {
  const theme = useTheme();
  const { classes } = useStyles();
  const { updateFeedback } = useUpdateFeedback();
  const mentionEntityContext = useContext(MentionEntityContext);
  const workflowArtifacts = useMemo(
    () =>
      [...mentionEntityContext.workflows.values()].flatMap(workflow => {
        if (workflow.__typename !== 'Workflow') return [];
        return [
          {
            workflowId: workflow.id,
            workflowTitle: workflow.name || 'Untitled Workflow',
          },
        ];
      }),
    [mentionEntityContext.workflows]
  );
  const taskArtifacts = useMemo(
    () =>
      [...mentionEntityContext.tasks.values()].flatMap(task => {
        if (task.__typename !== 'Task') return [];
        return [{ taskId: task.id, taskTitle: task.title || 'Untitled Task' }];
      }),
    [mentionEntityContext.tasks]
  );
  const workflowTemplateArtifacts = useMemo(
    () =>
      [...mentionEntityContext.workflows.values()].flatMap(workflow => {
        if (workflow.__typename !== 'Workflow') return [];
        return [
          {
            templateTitle: workflow.name || 'Untitled Workflow',
            templateWorkflowId: workflow.id,
          },
        ];
      }),
    [mentionEntityContext.workflows]
  );
  const playbookRunArtifacts = useMemo(
    () =>
      [...mentionEntityContext.documents.values()].flatMap(document => {
        if (document.__typename !== 'Document') return [];
        return [...mentionEntityContext.playbooks.values()].flatMap(playbook => {
          if (playbook.__typename !== 'Playbook') return [];
          return [
            {
              documentId: document.id,
              documentTitle: document.name || 'Untitled Document',
              playbookId: playbook.id,
              playbookTitle: playbook.title || 'Untitled Playbook',
            },
          ];
        });
      }),
    [mentionEntityContext.documents, mentionEntityContext.playbooks]
  );
  const isMarkedPositive = message.feedback === Apollo.AgentMessageFeedback.Positive;
  const isMarkedNegative = message.feedback === Apollo.AgentMessageFeedback.Negative;
  const { copyMarkdownPromiseToClipboard, createNewDocumentFromMarkdown } =
    useAssistantMessageHandlers();

  return (
    <div className={classes.container}>
      <AlineTooltip arrow placement="top" title="Create Aline document from AI message">
        <IconButton
          className={classes.actionButton}
          size="small"
          onClick={async () => {
            const messageMarkdown = await getAssistantActionMarkdown({
              assistantMessageRunning: !getIsTerminalAssistantStatus(message.status),
              messageId: message.id,
              messageText: message.text,
              planState,
              playbookRunArtifacts,
              taskArtifacts,
              threadId,
              uiParts: message.data?.uiParts,
              workflowArtifacts,
              workflowTemplateArtifacts,
            });
            await createNewDocumentFromMarkdown('', messageMarkdown, {
              aiChatItem: createAIChatItem({
                messageId: message.id,
                threadId,
                type: 'message',
              }),
            });
          }}
        >
          <FilePlus2 size={16} stroke="currentColor" strokeWidth={1.5} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Copy AI message to clipboard">
        <IconButton
          className={classes.actionButton}
          size="small"
          onClick={() => {
            /**
             * Hand the pending markdown promise straight to the clipboard so
             * the write starts inside the click gesture; awaiting it first
             * loses transient user activation on Safari/Firefox.
             */
            copyMarkdownPromiseToClipboard(
              getAssistantActionMarkdown({
                assistantMessageRunning: !getIsTerminalAssistantStatus(message.status),
                messageId: message.id,
                messageText: message.text,
                planState,
                playbookRunArtifacts,
                taskArtifacts,
                threadId,
                uiParts: message.data?.uiParts,
                workflowArtifacts,
                workflowTemplateArtifacts,
              })
            );
          }}
        >
          <Copy size={16} stroke="currentColor" strokeWidth={1.5} />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip
        arrow
        placement="top"
        title={isMarkedPositive ? 'AI response marked positive' : 'Mark AI response positive'}
      >
        <IconButton
          className={classes.actionButton}
          size="small"
          onClick={async () => {
            await updateFeedback({
              feedback: !isMarkedPositive ? Apollo.AgentMessageFeedback.Positive : null,
              messageId: message.id,
            });
          }}
        >
          <ThumbsUp
            color={isMarkedPositive ? theme.palette.success.main : undefined}
            fill={isMarkedPositive ? theme.palette.success.main : 'none'}
            size={16}
            strokeWidth={1.5}
          />
        </IconButton>
      </AlineTooltip>
      <AlineTooltip
        arrow
        placement="top"
        title={isMarkedNegative ? 'AI response marked negative' : 'Mark AI response negative'}
      >
        <IconButton
          className={classes.actionButton}
          size="small"
          onClick={async () => {
            await updateFeedback({
              feedback: !isMarkedNegative ? Apollo.AgentMessageFeedback.Negative : null,
              messageId: message.id,
            });
          }}
        >
          <ThumbsDown
            color={isMarkedNegative ? theme.palette.error.main : undefined}
            fill={isMarkedNegative ? theme.palette.error.main : 'none'}
            size={16}
            strokeWidth={1.5}
          />
        </IconButton>
      </AlineTooltip>
    </div>
  );
}

function ThinkingMessage({ thinkingMessage }: { thinkingMessage: string }) {
  return <PulsingChatStatus text={thinkingMessage} />;
}

const usePulseStyles = makeStyles()(theme => ({
  loadingMessage: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
  },
  waitAlert: {
    '& .MuiAlert-icon': {
      alignItems: 'center',
      paddingTop: 0,
    },
    marginTop: theme.spacing(1),
  },
  waitAlertIcon: {
    alignItems: 'center',
    paddingBottom: 0,
    paddingTop: 0,
  },
}));

/**
 * Durable OCR-wait banner driven by
 * message.data.documentExtractionPause.phase === 'prePlanning' (survives
 * refresh). Rendered pinned at the top of the ChatPanel, not inline in the
 * message; live LoadingEvent copy is suppressed while this shows.
 */
export function DocumentExtractionWaitBanner({ className }: { className?: string }): JSX.Element {
  const { classes, cx } = usePulseStyles();
  return (
    <AlineAlert
      className={cx(classes.waitAlert, className)}
      classes={{ icon: classes.waitAlertIcon }}
      icon={<PulseIcon variant={PulseIconVariant.LoadingPrimary} />}
      severity="info"
    >
      {DOCUMENT_EXTRACTION_WAIT_COPY}
    </AlineAlert>
  );
}

export function PulsingChatStatus({
  text,
  ...rest
}: { text: string } & React.HTMLAttributes<HTMLDivElement>) {
  const { classes } = usePulseStyles();

  return (
    <div className={classes.loadingMessage} {...rest}>
      <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
      <Typography>{text}</Typography>
    </div>
  );
}

const useErrorStyles = makeStyles()(theme => ({
  errorMessage: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

function AssistantErrorInMessage({ message }: { message: ChatAssistantMessage }) {
  const { classes } = useErrorStyles();

  if (message.text) {
    return (
      <div id={message.id}>
        <ChatPanelMarkdown
          completionText={message.text}
          documentInView={null}
          message={message}
          threadId=""
        />
      </div>
    );
  }

  return (
    <div className={classes.errorMessage} id={message.id}>
      <ErrorIcon />
      <Typography align="center">Aline AI encountered an error.</Typography>
    </div>
  );
}
